import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Car, Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiHelpers } from '@/lib/api';

const blankVehicle = () => ({
  id: `veh-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  year: '',
  make: '',
  model: '',
  color: '',
  vin: '',
  plate: '',
  notes: '',
});

const formatVehicle = (v) =>
  [v.year, v.color, v.make, v.model].filter(Boolean).join(' ').trim() || 'Untitled vehicle';

/**
 * Vehicles section on the customer profile — only renders when the org niche
 * includes "detailing". Lets contractors track which cars belong to each
 * customer so they can pull them up faster on the next service.
 */
export default function VehiclesSection({ orgId, client, onUpdated }) {
  const vehicles = client?.vehicles || [];
  const [editing, setEditing] = useState(null); // null | new | <id>
  const [draft, setDraft] = useState(blankVehicle());
  const [saving, setSaving] = useState(false);

  const open = (v) => {
    if (v) {
      setEditing(v.id);
      setDraft({ ...v });
    } else {
      setEditing('new');
      setDraft(blankVehicle());
    }
  };

  const close = () => {
    setEditing(null);
    setDraft(blankVehicle());
  };

  const save = async () => {
    if (!draft.make && !draft.model) {
      toast.error('Please enter at least the make or model');
      return;
    }
    setSaving(true);
    try {
      const next =
        editing === 'new'
          ? [...vehicles, draft]
          : vehicles.map((v) => (v.id === editing ? { ...draft } : v));
      await apiHelpers.updateClient(orgId, client.id, { vehicles: next });
      toast.success(editing === 'new' ? 'Vehicle added' : 'Vehicle updated');
      onUpdated?.();
      close();
    } catch (e) {
      console.error(e);
      toast.error('Could not save vehicle');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this vehicle?')) return;
    try {
      const next = vehicles.filter((v) => v.id !== id);
      await apiHelpers.updateClient(orgId, client.id, { vehicles: next });
      toast.success('Vehicle removed');
      onUpdated?.();
    } catch (e) {
      console.error(e);
      toast.error('Could not remove vehicle');
    }
  };

  return (
    <div className="card-default" data-testid="vehicles-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center">
            <Car className="w-4 h-4" />
          </div>
          <h2 className="font-heading text-lg sm:text-xl font-semibold">
            Vehicles
            <span className="ml-2 text-xs font-medium text-muted-foreground">
              ({vehicles.length})
            </span>
          </h2>
        </div>
        <Button size="sm" onClick={() => open(null)} data-testid="vehicles-add-btn">
          <Plus className="w-4 h-4 mr-1" /> Add vehicle
        </Button>
      </div>

      {vehicles.length === 0 ? (
        <div className="text-center py-8 text-sm text-muted-foreground">
          No vehicles on file yet. Add one to pull it up faster on the next service.
        </div>
      ) : (
        <div className="grid gap-2">
          {vehicles.map((v) => (
            <div
              key={v.id}
              className="flex items-start justify-between p-3 rounded-xl border border-gray-200 hover:bg-gray-50/60 transition-colors"
              data-testid={`vehicle-card-${v.id}`}
            >
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm">{formatVehicle(v)}</p>
                <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                  {v.plate && (
                    <span>
                      <span className="font-semibold uppercase tracking-wide text-[10px]">Plate:</span> {v.plate}
                    </span>
                  )}
                  {v.vin && (
                    <span>
                      <span className="font-semibold uppercase tracking-wide text-[10px]">VIN:</span> {v.vin}
                    </span>
                  )}
                </div>
                {v.notes && (
                  <p className="text-xs text-gray-600 mt-1.5 italic">"{v.notes}"</p>
                )}
              </div>
              <div className="flex items-center gap-1 ml-2">
                <Button variant="ghost" size="icon" onClick={() => open(v)} data-testid={`vehicle-edit-${v.id}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => remove(v.id)}
                  className="text-muted-foreground hover:text-destructive"
                  data-testid={`vehicle-delete-${v.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Add vehicle' : 'Edit vehicle'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Year</Label>
                <Input
                  value={draft.year}
                  onChange={(e) => setDraft({ ...draft, year: e.target.value })}
                  placeholder="2023"
                  className="mt-1"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Make</Label>
                <Input
                  value={draft.make}
                  onChange={(e) => setDraft({ ...draft, make: e.target.value })}
                  placeholder="Toyota"
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Model</Label>
                <Input
                  value={draft.model}
                  onChange={(e) => setDraft({ ...draft, model: e.target.value })}
                  placeholder="Tundra"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">Color</Label>
                <Input
                  value={draft.color}
                  onChange={(e) => setDraft({ ...draft, color: e.target.value })}
                  placeholder="Silver"
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Plate</Label>
                <Input
                  value={draft.plate}
                  onChange={(e) => setDraft({ ...draft, plate: e.target.value.toUpperCase() })}
                  placeholder="ABC-1234"
                  className="mt-1 uppercase"
                />
              </div>
              <div>
                <Label className="text-xs">VIN <span className="text-muted-foreground">(optional)</span></Label>
                <Input
                  value={draft.vin}
                  onChange={(e) => setDraft({ ...draft, vin: e.target.value.toUpperCase() })}
                  placeholder="1HGBH41JXMN..."
                  className="mt-1 uppercase font-mono text-xs"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea
                value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                placeholder="Heavy pet hair, smoker, custom rims, etc."
                rows={2}
                className="mt-1 resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="vehicle-save-btn">
              {saving ? 'Saving…' : 'Save vehicle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
